import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Subtitle } from "../../../components/Desc";
import ProgressIndicator from "../../../components/ProgressIndicator";
import strings from "../../../config/localization";
import {
  useAutoPersonnelAllocationsMutation,
  useAutoResourceAllocationsMutation,
  useGetTimeslotsQuery,
} from "../../../services/appln";

interface Timeslot {
  key: string;
  name: string;
  desc: string;
  startTime: string;
  endTime: string;
  personnelAssessmentCount: number;
  resourceAssessmentCount: number;
  personnelAllocationCount: number;
  resourceAllocationCount: number;
}

interface Data {
  rowData: Timeslot[];
  columnData: ColumnsType<Timeslot>;
}

export default function AllocationSummary(props: any) {
  const navigate = useNavigate();

  const [autoAllocatePersonnel, autoAllocatePersonnelParams] =
    useAutoPersonnelAllocationsMutation();

  const [autoAllocateResources, autoAllocateResourcesParams] =
    useAutoResourceAllocationsMutation();

  const bandobastContext = JSON.parse(localStorage.getItem("bandobast")!);

  const [tableData, setTableData] = React.useState<Data>({
    rowData: [],
    columnData: [],
  });

  const timeslotsQueryResponse = useGetTimeslotsQuery(props.bandobastId);

  React.useEffect(() => {
    if (!timeslotsQueryResponse.isSuccess) {
      return;
    }

    const rowData = timeslotsQueryResponse.data.data.map<Timeslot>((e: any) => {
      return {
        key: e.id,
        name: e.name,
        desc: e.desc,
        startTime: e.startTime,
        endTime: e.endTime,
        personnelAssessmentCount: e.personnelAssessmentCount,
        personnelAllocationCount: e.personnelAllocationCount,
        resourceAssessmentCount: e.resourceAssessmentCount,
        resourceAllocationCount: e.resourceAllocationCount,
      };
    });

    const columns: ColumnsType<Timeslot> = [
      {
        title: strings.name,
        dataIndex: "name",
        key: "name",
        width: 10,
      },
      {
        title: strings.personnelAssessmentCount,
        dataIndex: "personnelAssessmentCount",
        key: "personnelAssessmentCount",
        align: "center",
        width: 5,
      },
      {
        title: strings.personnelAllocationCount,
        dataIndex: "personnelAllocationCount",
        key: "personnelAllocationCount",
        align: "center",
        width: 5,
        render: (text, record, index) => (
          <Button
            onClick={() => {
              const toStore = {
                id: record.key,
                name: record.name,
                startTime: record.startTime,
                endTime: record.endTime,
              };
              localStorage.setItem("timeslot", JSON.stringify(toStore));
              navigate(`../timeslotSummary/${props.bandobastId}/${record.key}`);
            }}
          >
            {text as string}
          </Button>
        ),
      },
      {
        title: strings.resourceAssessmentCount,
        dataIndex: "resourceAssessmentCount",
        key: "resourceAssessmentCount",
        align: "center",
        width: 5,
      },
      {
        title: strings.resourceAllocationCount,
        dataIndex: "resourceAllocationCount",
        key: "resourceAllocationCount",
        align: "center",
        width: 5,
        render: (text, record, index) => (
          <Button
            onClick={() => {
              const toStore = {
                id: record.key,
                name: record.name,
                startTime: record.startTime,
                endTime: record.endTime,
              };
              localStorage.setItem("timeslot", JSON.stringify(toStore));
              navigate(
                `../timeslotResAllocationSummary/${props.bandobastId}/${record.key}`,
              );
            }}
          >
            {text as string}
          </Button>
        ),
      },
    ];

    setTableData({
      rowData: rowData,
      columnData: columns,
    });
  }, [timeslotsQueryResponse]);

  return (
    <Box sx={{ flexGrow: 1, width: "100%" }}>
      <Grid container spacing={2}>
        {(timeslotsQueryResponse.isLoading ||
          timeslotsQueryResponse.isFetching ||
          autoAllocatePersonnelParams.isLoading) && <ProgressIndicator />}
        <>
          <Grid item xs={12}>
            <Button
              variant="contained"
              id={"autoAllocate"}
              onClick={() => {
                autoAllocatePersonnel({
                  bandobastId: props.bandobastId,
                  timeslotId: "",
                });
                autoAllocateResources({
                  bandobastId: props.bandobastId,
                  timeslotId: "",
                });
              }}
            >
              {strings.allocate}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Subtitle text={strings.timeslots} />
          </Grid>

          <Grid item xs={12}>
            <Table
              virtual
              scroll={{ y: 600 }}
              columns={tableData.columnData}
              dataSource={tableData.rowData}
              pagination={false}
              summary={(pageData) => {
                let totalPersonnelAssessmentCount = 0;
                let totalPersonnelAllocationCount = 0;
                let totalResourceAssessmentCount = 0;
                let totalResourceAllocationCount = 0;

                pageData.forEach((e: any) => {
                  totalPersonnelAssessmentCount += e.personnelAssessmentCount;
                  totalPersonnelAllocationCount += e.personnelAllocationCount;
                  totalResourceAssessmentCount += e.resourceAssessmentCount;
                  totalResourceAllocationCount += e.resourceAllocationCount;
                });
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <b>{strings.total}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center" index={4}>
                        <b>{totalPersonnelAssessmentCount}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center" index={5}>
                        <b>{totalPersonnelAllocationCount}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center" index={4}>
                        <b>{totalResourceAssessmentCount}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center" index={5}>
                        <b>{totalResourceAllocationCount}</b>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Grid>
        </>
      </Grid>
    </Box>
  );
}
